import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { boxService } from '@/services/boxService';
import { boxTypeService } from '@/services/boxTypeService';
import { deviceService } from '@/services/deviceService';
import { deviceTypeService } from '@/services/deviceTypeService';
import { attributeService } from '@/services/attributeService';
import { attributeTypeService } from '@/services/attributeTypeService';
import { projectService } from '@/services/projectService';
import { formatDate } from '@/utils';
import { Box, BoxType, Device, Attribute, DeviceType, AttributeType } from '@/types';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';

const boxSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  code: z.string().min(1, 'Le code est requis'),
  ipaddress: z.string().optional(),
  macAddress: z.string().optional(),
  uuid: z.string().optional(),
  coordinate: z.string().optional(),
  type: z.string().min(1, 'Le type est requis'),
  isOnline: z.boolean().default(false),
  isVisible: z.boolean().default(true),
});

const deviceSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  code: z.string().min(1, 'Le code est requis'),
  uuid: z.string().optional(),
  type: z.string().min(1, 'Le type est requis'),
  box: z.string().min(1, 'La box est requise'),
});

const attributeSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  type: z.string().min(1, 'Le type est requis'),
  uuid: z.string().optional(),
  visible: z.boolean().default(true),
});

export function EquipmentsManagementPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBox, setEditingBox] = useState<Box | null>(null);
  const [selectedBox, setSelectedBox] = useState<Box | null>(null);
  const [expandedDevices, setExpandedDevices] = useState<Set<number>>(new Set());
  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false);
  const [isEditDeviceModalOpen, setIsEditDeviceModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [isAddAttributeModalOpen, setIsAddAttributeModalOpen] = useState(false);
  const [isEditAttributeModalOpen, setIsEditAttributeModalOpen] = useState(false);
  const [selectedDeviceForAttribute, setSelectedDeviceForAttribute] = useState<Device | null>(null);
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null);
  const [deviceAttributes, setDeviceAttributes] = useState<Record<number, Attribute[]>>({});
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const deviceFormInitialLoad = useRef(true);
  const queryClient = useQueryClient();
  const isInitialLoad = useRef(true);

  // Reset page to 1 when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, statusFilter, typeFilter]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<z.infer<typeof boxSchema>>({
    resolver: zodResolver(boxSchema),
    defaultValues: {
      name: '',
      code: '',
      ipaddress: '',
      macAddress: '',
      uuid: '',
      coordinate: '',
      type: '',
      isOnline: false,
      isVisible: true,
    },
  });

  // Device form
  const {
    register: registerDevice,
    handleSubmit: handleSubmitDevice,
    formState: { errors: deviceErrors },
    reset: resetDevice,
    control: controlDevice,
    watch: watchDevice,
    setValue: setValueDevice,
  } = useForm<z.infer<typeof deviceSchema>>({
    resolver: zodResolver(deviceSchema),
    defaultValues: {
      name: '',
      code: '',
      uuid: '',
      type: '',
      box: selectedBox ? String(selectedBox.id) : '',
    },
  });

  // Attribute form
  const {
    register: registerAttribute,
    handleSubmit: handleSubmitAttribute,
    formState: { errors: attributeErrors },
    reset: resetAttribute,
    control: controlAttribute,
    setValue: setValueAttribute,
    watch: watchAttribute,
  } = useForm<z.infer<typeof attributeSchema>>({
    resolver: zodResolver(attributeSchema),
    defaultValues: {
      name: '',
      type: '',
      uuid: '',
      visible: true,
    },
  });

  // Fetch project
  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProject(Number(projectId)),
    enabled: !!projectId,
  });

  // Fetch boxes statistics from dedicated endpoint
  const { data: stats = { total: 0, online: 0, offline: 0 }, isLoading: statsLoading } = useQuery({
    queryKey: ['boxes', 'stats', projectId],
    queryFn: () => projectService.getBoxesStats(Number(projectId)),
    enabled: !!projectId,
  });

  // Fetch all boxes for statistics (without search filter) - kept for compatibility
  const { data: allBoxes = [], isLoading: allBoxesLoading } = useQuery({
    queryKey: ['boxes', projectId, 'all'],
    queryFn: () => boxService.getBoxesByProject(Number(projectId)),
    enabled: !!projectId,
  });

  // Fetch boxes with search, filters and pagination (API-side filtering) for table display
  const {
    data: boxesData,
    isLoading: boxesLoading,
  } = useQuery({
    queryKey: ['boxes', projectId, debouncedSearchQuery, statusFilter, typeFilter, currentPage],
    queryFn: () => boxService.getBoxesByProject(
      Number(projectId),
      debouncedSearchQuery || undefined,
      currentPage,
      itemsPerPage,
      statusFilter !== 'all' ? statusFilter : undefined,
      typeFilter !== 'all' ? typeFilter : undefined
    ),
    enabled: !!projectId,
  });

  const boxes = boxesData?.data || [];
  const pagination = boxesData ? {
    totalItems: boxesData.totalItems,
    currentPage: boxesData.currentPage,
    itemsPerPage: boxesData.itemsPerPage,
    totalPages: boxesData.totalPages,
    hasNextPage: boxesData.hasNextPage,
    hasPreviousPage: boxesData.hasPreviousPage,
  } : null;

  // Fetch box types
  const { data: boxTypes = [] } = useQuery({
    queryKey: ['boxTypes'],
    queryFn: () => boxTypeService.getBoxTypes(),
  });

  // Fetch device types
  const { data: deviceTypes = [] } = useQuery({
    queryKey: ['deviceTypes'],
    queryFn: () => deviceTypeService.getDeviceTypes(),
  });

  // Fetch attribute types
  const { data: attributeTypes = [] } = useQuery({
    queryKey: ['attributeTypes'],
    queryFn: () => attributeTypeService.getAttributeTypes(),
  });

  // Toast states for boxes
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  // Toast states for devices
  const [deviceToastMessage, setDeviceToastMessage] = useState<string>('');
  const [deviceToastType, setDeviceToastType] = useState<'success' | 'error'>('success');
  const [showDeviceToast, setShowDeviceToast] = useState(false);

  // Toast states for attributes
  const [attributeToastMessage, setAttributeToastMessage] = useState<string>('');
  const [attributeToastType, setAttributeToastType] = useState<'success' | 'error'>('success');
  const [showAttributeToast, setShowAttributeToast] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data: z.infer<typeof boxSchema>) => {
      const payload: any = {
        name: data.name,
        code: data.code,
        project: `/api/projects/${projectId}`,
        type: `/api/box_types/${data.type}`,
        isOnline: data.isOnline,
        isVisible: data.isVisible,
      };
      if (data.ipaddress) payload.ipaddress = data.ipaddress;
      if (data.macAddress) payload.macAddress = data.macAddress;
      if (data.uuid) payload.uuid = data.uuid;
      if (data.coordinate) payload.coordinate = data.coordinate;
      return boxService.createBox(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId, 'all'] });
      setToastMessage('Équipement créé avec succès !');
      setToastType('success');
      setShowToast(true);
      setIsCreateModalOpen(false);
      reset();
      isInitialLoad.current = true;
    },
    onError: () => {
      setToastMessage('Erreur lors de la création de l\'équipement');
      setToastType('error');
      setShowToast(true);
    },
  });

  // Update box mutation
  const updateMutation = useMutation({
    mutationFn: (data: z.infer<typeof boxSchema>) => {
      if (!editingBox) throw new Error('No box selected for editing');
      const payload: any = {
        name: data.name,
        code: data.code, // Always send code as it's required
        project: `/api/projects/${projectId}`,
        type: `/api/box_types/${data.type}`,
        isOnline: data.isOnline,
        isVisible: data.isVisible,
      };
      if (data.ipaddress) payload.ipaddress = data.ipaddress;
      if (data.macAddress) payload.macAddress = data.macAddress;
      if (data.uuid) payload.uuid = data.uuid;
      if (data.coordinate) payload.coordinate = data.coordinate;
      return boxService.updateBox(editingBox.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId, 'all'] });
      setToastMessage('Équipement modifié avec succès !');
      setToastType('success');
      setShowToast(true);
      setIsEditModalOpen(false);
      setEditingBox(null);
      reset();
      isInitialLoad.current = true;
    },
    onError: () => {
      setToastMessage('Erreur lors de la modification de l\'équipement');
      setToastType('error');
      setShowToast(true);
    },
  });

  // Function to generate code from name
  const generateCodeFromName = (name: string): string => {
    return name
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '-') // Replace spaces with dashes
      .replace(/[^A-Z0-9-]/g, '') // Remove special characters except dashes
      .replace(/-+/g, '-') // Replace multiple dashes with single dash
      .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
  };

  // Watch name field and auto-generate code (only for create mode)
  const watchedName = watch('name');
  useEffect(() => {
    // Only generate code if create modal is open and it's not the initial load (user is typing)
    if (isCreateModalOpen && !isEditModalOpen && !isInitialLoad.current && watchedName && watchedName.trim()) {
      const generatedCode = generateCodeFromName(watchedName);
      setValue('code', generatedCode, { shouldValidate: false });
    }
  }, [watchedName, setValue, isCreateModalOpen, isEditModalOpen]);

  // Watch device name field and auto-generate code
  const watchedDeviceName = watchDevice('name');
  useEffect(() => {
    if (isAddDeviceModalOpen && !deviceFormInitialLoad.current && watchedDeviceName && watchedDeviceName.trim()) {
      const generatedCode = generateCodeFromName(watchedDeviceName);
      setValueDevice('code', generatedCode, { shouldValidate: false });
    }
  }, [watchedDeviceName, setValueDevice, isAddDeviceModalOpen]);

  // Reset initial load flag when modal opens/closes
  useEffect(() => {
    if (isCreateModalOpen || isEditModalOpen) {
      // Reset flag after a short delay to allow form to initialize
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 100);
    } else {
      isInitialLoad.current = true;
    }
  }, [isCreateModalOpen, isEditModalOpen]);

  // Reset device form initial load flag
  useEffect(() => {
    if (isAddDeviceModalOpen) {
      setTimeout(() => {
        deviceFormInitialLoad.current = false;
      }, 100);
    } else {
      deviceFormInitialLoad.current = true;
    }
  }, [isAddDeviceModalOpen]);

  // Update box field when selectedBox changes
  useEffect(() => {
    if (isAddDeviceModalOpen && selectedBox) {
      setValueDevice('box', String(selectedBox.id), { shouldValidate: false });
    }
  }, [selectedBox, isAddDeviceModalOpen, setValueDevice]);

  // Pre-fill attribute form when editing
  useEffect(() => {
    if (isEditAttributeModalOpen && editingAttribute) {
      // Extract attribute type ID - handle multiple formats
      let attributeTypeId: string = '';
      
      // Check if type is an object with id property
      if (editingAttribute.type && typeof editingAttribute.type === 'object') {
        const typeObj = editingAttribute.type as AttributeType;
        if (typeObj.id) {
          attributeTypeId = String(typeObj.id);
        } else if ((typeObj as any)['@id']) {
          // Handle JSON-LD format with @id
          const match = String((typeObj as any)['@id']).match(/\/attribute_types\/(\d+)/);
          if (match) {
            attributeTypeId = match[1];
          }
        }
      } else if (typeof editingAttribute.type === 'string') {
        // Handle IRI string format
        const match = editingAttribute.type.match(/\/attribute_types\/(\d+)/);
        if (match) {
          attributeTypeId = match[1];
        }
      }
      
      // Reset form with attribute values
      resetAttribute({
        name: editingAttribute.name || '',
        type: attributeTypeId,
        uuid: editingAttribute.uuid || '',
        visible: editingAttribute.visible ?? true,
      });
      
      // Ensure type is set correctly after form reset
      if (attributeTypeId) {
        // Use multiple timeouts to ensure the select is updated
        setTimeout(() => {
          setValueAttribute('type', attributeTypeId, { shouldValidate: false });
          // Force update again after a short delay
          setTimeout(() => {
            setValueAttribute('type', attributeTypeId, { shouldValidate: false });
          }, 50);
        }, 100);
      }
    } else if (!isEditAttributeModalOpen) {
      // Reset form when modal closes
      resetAttribute({
        name: '',
        type: '',
        uuid: '',
        visible: true,
      });
    }
  }, [isEditAttributeModalOpen, editingAttribute, resetAttribute, setValueAttribute]);

  // Clear device attributes when box selection changes (but don't load them automatically)
  useEffect(() => {
    if (!selectedBox) {
      // Clear all device attributes when no box is selected
      setDeviceAttributes({});
      setExpandedDevices(new Set());
    }
  }, [selectedBox]);

  // Handle edit device button click
  const handleEditDeviceClick = async (device: Device) => {
    // Fetch full device data from API to get all fields (uuid, type, etc.)
    let fullDevice = device;
    try {
      // Extract device ID from @id if needed
      let deviceId = device.id;
      if (!deviceId && (device as any)['@id']) {
        const match = String((device as any)['@id']).match(/\/devices\/(\d+)/);
        if (match) {
          deviceId = parseInt(match[1], 10);
        }
      }
      
      if (deviceId) {
        fullDevice = await deviceService.getDevice(deviceId);
      }
    } catch (error) {
      console.error('Error fetching device:', error);
      // Fallback to partial device data
    }

    // Extract type ID from fullDevice.type (could be object or IRI string)
    let typeId = '';
    if (typeof fullDevice.type === 'string') {
      // If it's an IRI string, extract the ID
      const match = fullDevice.type.match(/\/device_types\/(\d+)/);
      typeId = match ? match[1] : '';
    } else if (fullDevice.type && typeof fullDevice.type === 'object') {
      const deviceType = fullDevice.type as any;
      // Check for @id (JSON-LD format)
      if (deviceType['@id']) {
        const match = String(deviceType['@id']).match(/\/device_types\/(\d+)/);
        typeId = match ? match[1] : '';
      } else if (deviceType.id !== undefined && deviceType.id !== null) {
        // Direct id property
        typeId = String(deviceType.id);
      }
    }

    // Extract box ID from fullDevice.box
    let boxId = '';
    if (typeof fullDevice.box === 'string') {
      const match = fullDevice.box.match(/\/boxes\/(\d+)/);
      boxId = match ? match[1] : '';
    } else if (fullDevice.box && typeof fullDevice.box === 'object') {
      const box = fullDevice.box as any;
      if (box['@id']) {
        const match = String(box['@id']).match(/\/boxes\/(\d+)/);
        boxId = match ? match[1] : '';
      } else if (box.id !== undefined && box.id !== null) {
        boxId = String(box.id);
      }
    }

    // Reset form with device data
    const formData = {
      name: fullDevice.name || '',
      code: fullDevice.code || '',
      uuid: fullDevice.uuid || '',
      type: typeId,
      box: boxId || (selectedBox ? String(selectedBox.id) : ''),
    };

    resetDevice(formData);
    setEditingDevice(fullDevice);
    setIsEditDeviceModalOpen(true);
    deviceFormInitialLoad.current = true;

    // Force update the form after a short delay to ensure selects are updated
    setTimeout(() => {
      if (typeId) {
        setValueDevice('type', typeId, { shouldValidate: false, shouldDirty: false });
      }
      if (boxId) {
        setValueDevice('box', boxId, { shouldValidate: false, shouldDirty: false });
      }
      // Also set UUID explicitly
      if (fullDevice.uuid) {
        setValueDevice('uuid', fullDevice.uuid, { shouldValidate: false, shouldDirty: false });
      }
    }, 200);
  };

  // Handle edit button click
  const handleEditClick = (box: Box) => {
    setEditingBox(box);
    // Extract type ID from box.type (could be object or IRI string)
    let typeId = '';
    if (typeof box.type === 'string') {
      // If it's an IRI string, extract the ID
      const match = box.type.match(/\/box_types\/(\d+)/);
      typeId = match ? match[1] : '';
    } else if (box.type && typeof box.type === 'object') {
      const boxType = box.type as any;
      // Check for @id (JSON-LD format)
      if (boxType['@id']) {
        const match = String(boxType['@id']).match(/\/box_types\/(\d+)/);
        typeId = match ? match[1] : '';
      } else if (boxType.id !== undefined && boxType.id !== null) {
        // Direct id property
        typeId = String(boxType.id);
      }
    }

    // Reset form with box data - ensure typeId is a string
    const formData = {
      name: box.name || '',
      code: box.code || '',
      ipaddress: box.ipaddress || '',
      macAddress: box.macAddress || '',
      uuid: box.uuid || '',
      coordinate: box.coordinate || '',
      type: typeId, // This should be a string matching the option value
      isOnline: box.isOnline || false,
      isVisible: box.isVisible !== undefined ? box.isVisible : true,
    };

    reset(formData);
    setIsEditModalOpen(true);
    isInitialLoad.current = true;
    
    // Force update the form after a short delay to ensure the select is updated
    setTimeout(() => {
      if (typeId) {
        setValue('type', typeId, { shouldValidate: false });
      }
    }, 150);
  };

  const onSubmit = (data: z.infer<typeof boxSchema>) => {
    if (isEditModalOpen && editingBox) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  // Create device mutation
  const createDeviceMutation = useMutation({
    mutationFn: (data: z.infer<typeof deviceSchema>) => {
      const payload: any = {
        name: data.name,
        code: data.code, // Auto-generated, hidden from user
        box: `/api/boxes/${data.box}`,
        project: `/api/projects/${projectId}`, // Managed by code
        type: `/api/device_types/${data.type}`,
      };
      if (data.uuid) payload.uuid = data.uuid;
      return deviceService.createDevice(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId, 'all'] });
      // Refresh selected box to show new device
      if (selectedBox) {
        boxService.getBox(selectedBox.id).then((fullBox) => {
          setSelectedBox(fullBox);
        });
      }
      setDeviceToastMessage('Appareil créé avec succès !');
      setDeviceToastType('success');
      setShowDeviceToast(true);
      setIsAddDeviceModalOpen(false);
      resetDevice();
      deviceFormInitialLoad.current = true;
    },
    onError: () => {
      setDeviceToastMessage('Erreur lors de la création de l\'appareil');
      setDeviceToastType('error');
      setShowDeviceToast(true);
    },
  });

  // Update device mutation
  const updateDeviceMutation = useMutation({
    mutationFn: (data: z.infer<typeof deviceSchema>) => {
      if (!editingDevice) throw new Error('No device selected for editing');
      const payload: any = {
        name: data.name,
        code: data.code, // Always send code as it's required
        box: `/api/boxes/${data.box}`,
        project: `/api/projects/${projectId}`, // Managed by code
        type: `/api/device_types/${data.type}`,
      };
      if (data.uuid) payload.uuid = data.uuid;
      return deviceService.updateDevice(editingDevice.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId, 'all'] });
      // Refresh selected box to show updated device
      if (selectedBox) {
        boxService.getBox(selectedBox.id).then((fullBox) => {
          setSelectedBox(fullBox);
        });
      }
      setDeviceToastMessage('Appareil modifié avec succès !');
      setDeviceToastType('success');
      setShowDeviceToast(true);
      setIsEditDeviceModalOpen(false);
      setEditingDevice(null);
      resetDevice();
      deviceFormInitialLoad.current = true;
    },
    onError: () => {
      setDeviceToastMessage('Erreur lors de la modification de l\'appareil');
      setDeviceToastType('error');
      setShowDeviceToast(true);
    },
  });

  const onSubmitDevice = (data: z.infer<typeof deviceSchema>) => {
    if (isEditDeviceModalOpen && editingDevice) {
      updateDeviceMutation.mutate(data);
    } else {
      createDeviceMutation.mutate(data);
    }
  };

  // Create attribute mutation
  const createAttributeMutation = useMutation({
    mutationFn: (data: z.infer<typeof attributeSchema>) => {
      if (!selectedDeviceForAttribute || !selectedBox) throw new Error('No device or box selected');
      
      const deviceId = getDeviceId(selectedDeviceForAttribute);
      if (!deviceId) {
        throw new Error('Device ID is missing');
      }
      
      const payload: any = {
        name: data.name,
        type: `/api/attribute_types/${data.type}`,
        device: `/api/devices/${deviceId}`,
        project: `/api/projects/${projectId}`,
        visible: data.visible,
      };
      if (data.uuid) payload.uuid = data.uuid;
      return attributeService.createAttribute(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId, 'all'] });
      // Refresh selected box to show new attribute
      if (selectedBox) {
        boxService.getBox(selectedBox.id).then((fullBox) => {
          setSelectedBox(fullBox);
        });
      }
      // Refresh attributes for the device
      if (selectedDeviceForAttribute) {
        const deviceId = getDeviceId(selectedDeviceForAttribute);
        if (deviceId) {
          attributeService.getAttributesByDevice(deviceId).then((attributes) => {
            setDeviceAttributes((prev) => ({
              ...prev,
              [deviceId]: attributes,
            }));
          });
        }
      }
      setAttributeToastMessage('Attribut créé avec succès !');
      setAttributeToastType('success');
      setShowAttributeToast(true);
      setIsAddAttributeModalOpen(false);
      resetAttribute();
      setSelectedDeviceForAttribute(null);
    },
    onError: () => {
      setAttributeToastMessage('Erreur lors de la création de l\'attribut');
      setAttributeToastType('error');
      setShowAttributeToast(true);
    },
  });

  // Update attribute mutation
  const updateAttributeMutation = useMutation({
    mutationFn: (data: z.infer<typeof attributeSchema>) => {
      if (!editingAttribute || !selectedBox) throw new Error('No attribute or box selected for editing');
      
      // Extract device ID - handle both object and IRI string formats
      let deviceId: number | null = null;
      const device = editingAttribute.device;
      
      if (typeof device === 'string') {
        // Device is an IRI string like "/api/devices/1"
        const match = device.match(/\/devices\/(\d+)/);
        if (match) {
          deviceId = parseInt(match[1], 10);
        }
      } else if (device && typeof device === 'object') {
        // Device is an object
        deviceId = getDeviceId(device as Device);
      }
      
      if (!deviceId) {
        console.error('Device from attribute:', device);
        console.error('Editing attribute:', editingAttribute);
        throw new Error('Device ID is missing');
      }
      
      const payload: any = {
        name: data.name,
        type: `/api/attribute_types/${data.type}`,
        device: `/api/devices/${deviceId}`,
        project: `/api/projects/${projectId}`,
        visible: data.visible,
      };
      if (data.uuid) payload.uuid = data.uuid;
      
      console.log('Updating attribute with payload:', payload);
      console.log('Attribute ID:', editingAttribute.id);
      console.log('Device ID extracted:', deviceId);
      
      return attributeService.updateAttribute(editingAttribute.id, payload);
    },
    onSuccess: () => {
      console.log('Attribute updated successfully');
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
      queryClient.invalidateQueries({ queryKey: ['boxes', projectId, 'all'] });
      // Refresh selected box to show updated attribute
      if (selectedBox) {
        boxService.getBox(selectedBox.id).then((fullBox) => {
          setSelectedBox(fullBox);
        });
      }
      // Refresh attributes for the device
      if (selectedDeviceForAttribute) {
        const deviceId = getDeviceId(selectedDeviceForAttribute);
        if (deviceId) {
          attributeService.getAttributesByDevice(deviceId).then((attributes) => {
            setDeviceAttributes((prev) => ({
              ...prev,
              [deviceId]: attributes,
            }));
          });
        }
      }
      setIsEditAttributeModalOpen(false);
      resetAttribute();
      setEditingAttribute(null);
      setSelectedDeviceForAttribute(null);
    },
    onError: (error: any) => {
      console.error('Error updating attribute:', error);
      console.error('Error response:', error.response?.data);
      alert(`Erreur lors de la modification de l'attribut: ${error.response?.data?.detail || error.message}`);
    },
  });

  const onSubmitAttribute = (data: z.infer<typeof attributeSchema>) => {
    if (isEditAttributeModalOpen && editingAttribute) {
      updateAttributeMutation.mutate(data);
    } else {
      createAttributeMutation.mutate(data);
    }
  };

  // Handle edit attribute button click
  const handleEditAttributeClick = async (attribute: Attribute) => {
    console.log('handleEditAttributeClick called with:', attribute);
    
    // Use the device from the attribute directly
    const device = attribute.device as Device;
    if (!device) {
      console.error('Device is missing for attribute');
      return;
    }
    
    console.log('Device found:', device);
    console.log('SelectedBox:', selectedBox);
    
    // Find the device in selectedBox.devices if available, otherwise use attribute.device
    const deviceId = getDeviceId(device);
    console.log('Device ID:', deviceId);
    
    const deviceInBox = selectedBox?.devices.find((d) => getDeviceId(d) === deviceId);
    console.log('Device in box:', deviceInBox);
    
    // Set the device (prefer device from box if available, otherwise use attribute.device)
    const deviceToUse = deviceInBox || device;
    console.log('Device to use:', deviceToUse);
    setSelectedDeviceForAttribute(deviceToUse);
    
    // Load full attribute data from API to ensure we have complete type information
    let fullAttribute = attribute;
    try {
      if (attribute.id) {
        console.log('Loading full attribute data for ID:', attribute.id);
        fullAttribute = await attributeService.getAttribute(attribute.id);
        console.log('Full attribute loaded:', fullAttribute);
      }
    } catch (error) {
      console.error('Error loading full attribute data:', error);
      // Use the attribute we have if API call fails
    }
    
    // Set editing attribute - this will trigger useEffect to pre-fill form
    console.log('Setting editing attribute:', fullAttribute);
    setEditingAttribute(fullAttribute);
    
    // Open modal
    console.log('Opening edit modal');
    setIsEditAttributeModalOpen(true);
  };

  // Helper function to extract device ID
  const getDeviceId = (device: Device): number | null => {
    if (device.id) return device.id;
    if ((device as any)['@id']) {
      const match = String((device as any)['@id']).match(/\/devices\/(\d+)/);
      if (match) return parseInt(match[1], 10);
    }
    return null;
  };

  // Load attributes when device is expanded
  const handleDeviceExpand = async (device: Device) => {
    const deviceId = getDeviceId(device);
    if (!deviceId) {
      console.error('Device ID is missing');
      return;
    }
    
    const isExpanded = expandedDevices.has(deviceId);
    const newExpanded = new Set(expandedDevices);
    
    if (isExpanded) {
      newExpanded.delete(deviceId);
    } else {
      newExpanded.add(deviceId);
      // Load attributes if not already loaded
      if (!deviceAttributes[deviceId]) {
        try {
          const attributes = await attributeService.getAttributesByDevice(deviceId);
          // Debug: log attributes to check status values
          if (process.env.NODE_ENV === 'development') {
            console.log('Attributes loaded for device:', deviceId, attributes.map((a: Attribute) => ({
              id: a.id,
              name: a.name,
              status: a.status,
              statusType: typeof a.status
            })));
          }
          setDeviceAttributes((prev) => ({
            ...prev,
            [deviceId]: attributes,
          }));
        } catch (error) {
          console.error('Error loading attributes:', error);
        }
      }
    }
    setExpandedDevices(newExpanded);
  };

  // Statistics are now fetched from the API endpoint
  // No need to calculate from allBoxes anymore

  // Sort boxes by name (default sorting)
  const sortedBoxes = useMemo(() => {
    const sorted = [...boxes];
    return sorted.sort((a, b) => a.name.localeCompare(b.name));
  }, [boxes]);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusBadge = (isOnline: boolean) => {
    if (isOnline) {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
          En ligne
        </span>
      );
    }
    return (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800">
        Hors ligne
      </span>
    );
  };

  const getBoxTypeIcon = (type: any) => {
    const typeName = typeof type === 'string' ? type : type?.type || '';
    if (typeName === 'WES') {
      return 'settings_input_component';
    }
    return 'dns';
  };

  const getBoxTypeColor = (type: any) => {
    const typeName = typeof type === 'string' ? type : type?.type || '';
    if (typeName === 'WES') {
      return 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-300';
    }
    return 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300';
  };

  const getBoxLocation = (box: Box) => {
    if (box.areas && box.areas.length > 0) {
      const areaNames = box.areas.map((area) => area.name).join(' • ');
      return areaNames;
    }
    return project?.title || '';
  };

  if (!projectId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Project ID manquant</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  // Only show full page loader on initial project load, not on pagination/search
  if (projectLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Erreur lors du chargement du projet</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-slate-400 dark:text-slate-600">Admin</span> / Box GTB
            </div>
            {project && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
                {project.city && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          {/* Header Section */}
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Liste des Boîtiers</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Gérez l'ensemble des boîtiers de communication installés.
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex gap-3">
              <button className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg shadow-sm text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 focus:outline-none transition-colors">
                <span className="material-icons-outlined text-lg mr-2">filter_list</span>
                Filtres
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                <span className="material-icons-outlined text-lg mr-2">add</span>
                Nouveau Boîtier
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white dark:bg-[#16202A] p-6 rounded-xl border border-slate-200 dark:border-[#1E293B] shadow-sm flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Boîtiers</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-lg text-primary">
                <span className="material-icons-outlined">router</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#16202A] p-6 rounded-xl border border-slate-200 dark:border-[#1E293B] shadow-sm flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">En Ligne</p>
                <p className="text-2xl font-bold text-emerald-500 mt-1">{stats.online}</p>
              </div>
              <div className="p-3 bg-emerald-500/10 rounded-lg text-emerald-500">
                <span className="material-icons-outlined">wifi</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#16202A] p-6 rounded-xl border border-slate-200 dark:border-[#1E293B] shadow-sm flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Hors Ligne</p>
                <p className="text-2xl font-bold text-red-500 mt-1">{stats.offline}</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg text-red-500">
                <span className="material-icons-outlined">wifi_off</span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
            {/* Table Header with Search and Sort */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="relative w-full sm:w-72">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-icons-outlined text-slate-400">search</span>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-[#1E293B] rounded-md leading-5 bg-slate-50 dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm transition duration-150 ease-in-out"
                  placeholder="Rechercher un boîtier..."
                />
              </div>
              <div className="flex items-center gap-3">
                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Statut:</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'online' | 'offline')}
                    className="form-select block pl-3 pr-8 py-2 text-sm border-slate-300 dark:border-[#1E293B] focus:outline-none focus:ring-primary focus:border-primary rounded-md bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                  >
                    <option value="all">Tous</option>
                    <option value="online">En ligne</option>
                    <option value="offline">Hors ligne</option>
                  </select>
                </div>

                {/* Type Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Type:</span>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="form-select block pl-3 pr-8 py-2 text-sm border-slate-300 dark:border-[#1E293B] focus:outline-none focus:ring-primary focus:border-primary rounded-md bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white"
                  >
                    <option value="all">Tous</option>
                    {boxTypes.map((type) => (
                      <option key={type.id} value={String(type.id)}>
                        {type.type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-[#1E293B]">
                <thead className="bg-slate-50 dark:bg-[#0B1116]/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-10" scope="col">
                      <input className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary bg-transparent" type="checkbox" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" scope="col">
                      Nom du Boîtier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" scope="col">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" scope="col">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" scope="col">
                      Adresse IP
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" scope="col">
                      Créé le
                    </th>
                    <th className="relative px-6 py-3" scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#16202A] divide-y divide-slate-200 dark:divide-[#1E293B]">
                  {boxesLoading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                          <p className="text-slate-500 dark:text-slate-400">Chargement des boîtiers...</p>
                        </div>
                      </td>
                    </tr>
                  ) : sortedBoxes.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-slate-500 dark:text-slate-400">
                        Aucun boîtier trouvé
                      </td>
                    </tr>
                  ) : (
                    sortedBoxes.map((box) => {
                      const boxType = typeof box.type === 'string' ? box.type : (box.type as any)?.type || '';
                      return (
                        <tr key={box.id} className="hover:bg-slate-50 dark:hover:bg-[#0B1116]/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary bg-transparent"
                              type="checkbox"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`flex-shrink-0 h-10 w-10 rounded-lg ${getBoxTypeColor(box.type)} flex items-center justify-center`}>
                                <span className="material-icons-outlined">{getBoxTypeIcon(box.type)}</span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-slate-900 dark:text-white">{box.name}</div>
                                <div className="text-sm text-slate-500 dark:text-slate-400">{getBoxLocation(box)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">{boxType}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(box.isOnline)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 font-mono">
                            {box.ipaddress || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                            {box.createdAt ? formatDate(box.createdAt, 'dd MMM yyyy') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => {
                                  // Fetch full box details with devices using getBoxWithDevices
                                  // This ensures devices are loaded with their isOnline status
                                  boxService.getBoxWithDevices(box.id).then((fullBox) => {
                                    // Debug: log devices to check isOnline values
                                    if (process.env.NODE_ENV === 'development' && fullBox.devices) {
                                      console.log('Box devices loaded:', fullBox.devices.map((d: Device) => ({
                                        id: d.id,
                                        name: d.name,
                                        isOnline: d.isOnline,
                                        isOnlineType: typeof d.isOnline
                                      })));
                                    }
                                    setSelectedBox(fullBox);
                                  }).catch((error) => {
                                    console.error('Error loading box with devices:', error);
                                    // Fallback to regular getBox if getBoxWithDevices fails
                                    boxService.getBox(box.id).then((fullBox) => {
                                      setSelectedBox(fullBox);
                                    });
                                  });
                                }}
                                className="text-slate-400 hover:text-primary transition-colors"
                                title="Voir"
                              >
                                <span className="material-icons-outlined">visibility</span>
                              </button>
                              <button
                                onClick={() => handleEditClick(box)}
                                className="text-slate-400 hover:text-amber-500 transition-colors"
                                title="Modifier"
                              >
                                <span className="material-icons-outlined">edit</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Affichage de {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} à{' '}
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} sur {pagination.totalItems} boîtiers
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={!pagination.hasPreviousPage}
                    className="relative inline-flex items-center px-3 py-2 border border-slate-300 dark:border-[#1E293B] text-sm font-medium rounded-md bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#16202A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-icons-outlined text-lg">chevron_left</span>
                  </button>
                  {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(pageNum);
                        }}
                        aria-current={pagination.currentPage === pageNum ? 'page' : undefined}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.currentPage === pageNum
                            ? 'z-10 bg-primary/10 border-primary text-primary dark:text-primary'
                            : 'bg-white dark:bg-[#0B1116] border-slate-300 dark:border-[#1E293B] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#16202A]'
                        } rounded-md transition-colors`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage((p) => Math.min(pagination.totalPages, p + 1));
                    }}
                    disabled={!pagination.hasNextPage}
                    className="relative inline-flex items-center px-3 py-2 border border-slate-300 dark:border-[#1E293B] text-sm font-medium rounded-md bg-white dark:bg-[#0B1116] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#16202A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-icons-outlined text-lg">chevron_right</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create/Edit Box Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-4xl bg-[#1e293b] rounded-xl shadow-2xl border border-[#1E293B] overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-white/5">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <span className="material-icons-outlined text-primary">edit_note</span>
                {isEditModalOpen ? 'Modifier le boîtier' : 'Nouveau boîtier'}
              </h2>
              <button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setIsEditModalOpen(false);
                  setEditingBox(null);
                  reset();
                  isInitialLoad.current = true;
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 md:p-8">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Nom de l'équipement <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('name')}
                      className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary placeholder-slate-600 transition-shadow outline-none"
                      placeholder="Ex: Box Etage 1"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                      <span className="material-icons-outlined text-sm">qr_code</span>
                      Code technique (ID){' '}
                      <span className="text-xs text-slate-500 dark:text-slate-400">(lecture seule)</span>
                    </label>
                    <input
                      {...register('code')}
                      readOnly
                      className="w-full bg-[#0B1116]/50 border border-slate-700 text-slate-400 cursor-not-allowed rounded-lg px-4 py-2.5 focus:outline-none"
                      placeholder="Code technique"
                    />
                    {errors.code && (
                      <p className="mt-1 text-sm text-red-400">{errors.code.message}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Adresse IP</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                        <span className="material-icons-outlined text-sm">lan</span>
                      </span>
                      <input
                        {...register('ipaddress')}
                        className="w-full bg-[#0B1116] border border-slate-700 rounded-lg pl-9 pr-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary placeholder-slate-600 transition-shadow outline-none font-mono text-sm"
                        placeholder="192.168.1.1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Adresse MAC</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                        <span className="material-icons-outlined text-sm">qr_code</span>
                      </span>
                      <input
                        {...register('macAddress')}
                        className="w-full bg-[#0B1116] border border-slate-700 rounded-lg pl-9 pr-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary placeholder-slate-600 transition-shadow outline-none font-mono text-sm"
                        placeholder="00:1B:44:11:3A:B7"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Type d'équipement <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          key={`type-select-${editingBox?.id || 'new'}-${field.value || ''}`}
                          value={field.value || ''}
                          onChange={field.onChange}
                          className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary outline-none appearance-none"
                        >
                          <option value="">Sélectionner...</option>
                          {boxTypes.map((type) => (
                            <option key={type.id} value={String(type.id)}>
                              {type.type}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-400">{errors.type.message}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      UUID (Lecture seule)
                    </label>
                    <input
                      {...register('uuid')}
                      readOnly
                      className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-slate-400 focus:outline-none cursor-not-allowed font-mono text-sm"
                      placeholder="Non disponible"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      Dernière mise à jour (Lecture seule)
                    </label>
                    <input
                      className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-slate-400 focus:outline-none cursor-not-allowed"
                      readOnly
                      type="text"
                      value="Non disponible"
                    />
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-700/50 flex flex-col sm:flex-row gap-8">
                  <div className="flex items-center justify-between gap-4 bg-slate-800/50 p-3 rounded-lg border border-slate-700 w-full sm:w-auto">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-white">Visible</span>
                      <span className="text-xs text-slate-400">Afficher dans la liste</span>
                    </div>
                    <Controller
                      name="isVisible"
                      control={control}
                      render={({ field }) => (
                        <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                          <input
                            type="checkbox"
                            {...field}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            className="sr-only"
                            id="is_visible"
                          />
                          <label
                            htmlFor="is_visible"
                            className={`block w-10 h-5 rounded-full cursor-pointer transition-colors ${
                              field.value ? 'bg-primary' : 'bg-slate-600'
                            }`}
                          >
                            <span
                              className={`block h-5 w-5 rounded-full bg-white shadow-md transform transition-transform ${
                                field.value ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </label>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
            </form>
            <div className="px-6 py-4 bg-slate-900/50 border-t border-slate-700/50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setIsEditModalOpen(false);
                  setEditingBox(null);
                  reset();
                  isInitialLoad.current = true;
                }}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors border border-transparent hover:border-slate-600"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 shadow-lg shadow-teal-500/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="material-icons-outlined text-lg">save</span>
                {isEditModalOpen
                  ? updateMutation.isPending
                    ? 'Modification...'
                    : 'Modifier'
                  : createMutation.isPending
                    ? 'Création...'
                    : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Sidebar */}
      {selectedBox && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              setSelectedBox(null);
              setExpandedDevices(new Set());
            }}
          />
          {/* Sidebar */}
          <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-[#16202A] border-l border-slate-200 dark:border-[#1E293B] shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between bg-slate-50 dark:bg-[#0B1116]">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Détails de l'Équipement</h2>
            <button
              onClick={() => {
                setSelectedBox(null);
                setExpandedDevices(new Set());
              }}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
            >
              <span className="material-icons-outlined">close</span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Box Info */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">{selectedBox.name}</h3>
                {getStatusBadge(selectedBox.isOnline)}
              </div>
              {selectedBox.areas && selectedBox.areas.length > 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  {selectedBox.areas.map((area) => area.name).join(' • ')}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 dark:text-slate-400">CODE:</span>
                  <p className="text-slate-900 dark:text-white font-mono font-medium">{selectedBox.code}</p>
                </div>
                {selectedBox.ipaddress && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">IP ADDR:</span>
                    <p className="text-slate-900 dark:text-white font-mono">{selectedBox.ipaddress}</p>
                  </div>
                )}
                {selectedBox.macAddress && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">MAC:</span>
                    <p className="text-slate-900 dark:text-white font-mono">{selectedBox.macAddress}</p>
                  </div>
                )}
                {selectedBox.lastUpdate && (
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">UPTIME:</span>
                    <p className="text-slate-900 dark:text-white">{formatDate(selectedBox.lastUpdate)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Devices List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  APPAREILS CONNECTÉS ({selectedBox.devices?.length || 0})
                </h4>
                <button
                  onClick={() => setIsAddDeviceModalOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-teal-600 rounded-lg transition-colors"
                >
                  <span className="material-icons-outlined text-sm mr-1">add</span>
                  Ajouter
                </button>
              </div>
              {selectedBox.devices && selectedBox.devices.length > 0 ? (
                <div className="space-y-2">
                  {selectedBox.devices.map((device) => {
                    const deviceId = getDeviceId(device);
                    const isExpanded = deviceId ? expandedDevices.has(deviceId) : false;
                    const deviceType = typeof device.type === 'string' ? device.type : (device.type as any)?.type || '';
                    // Use only attributes loaded via deviceAttributes state, not device.attributes
                    // device.attributes might contain stale or incorrect data
                    const attributes = deviceId && deviceAttributes[deviceId] ? deviceAttributes[deviceId] : [];
                    // Ensure isOnline is a boolean - check both property and method
                    // Handle case where isOnline might be undefined or the property might not exist
                    const deviceIsOnline = device.isOnline === true || (device as any).isOnline === true;
                    
                    // Debug log to check device data
                    if (process.env.NODE_ENV === 'development') {
                      console.log('Device data:', {
                        id: deviceId,
                        name: device.name,
                        isOnline: device.isOnline,
                        deviceIsOnline,
                        fullDevice: device
                      });
                    }
                    
                    return (
                      <div
                        key={deviceId || `device-${Math.random()}`}
                        className="border border-slate-200 dark:border-[#1E293B] rounded-lg overflow-hidden bg-white dark:bg-[#0B1116]"
                      >
                        {/* Device Header */}
                        <div className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-[#16202A] transition-colors">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="p-2 bg-primary/10 rounded-lg text-primary">
                              <span className="material-icons-outlined text-lg">
                                {deviceType === 'THERMOSTAT' ? 'thermostat' : 'devices'}
                              </span>
                            </div>
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-medium text-slate-900 dark:text-white">
                                  {device.name || device.code || `Device ${deviceId || 'N/A'}`}
                                </p>
                                {/* Status badge */}
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    deviceIsOnline
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-red-500/20 text-red-400'
                                  }`}
                                >
                                  {deviceIsOnline ? 'En ligne' : 'Hors ligne'}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {attributes.length} Attributs
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Edit button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditDeviceClick(device);
                              }}
                              className="p-1.5 text-slate-400 hover:text-primary transition-colors rounded hover:bg-slate-100 dark:hover:bg-[#1E293B]"
                              title="Modifier l'équipement"
                            >
                              <span className="material-icons-outlined text-lg">edit</span>
                            </button>
                            {/* Expand/Collapse button */}
                            <button
                              onClick={() => handleDeviceExpand(device)}
                              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                            >
                              <span
                                className={`material-icons-outlined transition-transform ${
                                  isExpanded ? 'rotate-180' : ''
                                }`}
                              >
                                expand_more
                              </span>
                            </button>
                          </div>
                        </div>

                        {/* Device Attributes */}
                        {isExpanded && (
                          <div className="px-4 py-3 border-t border-slate-200 dark:border-[#1E293B] bg-slate-50 dark:bg-[#0B1116]">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                                Attributs
                              </h5>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDeviceForAttribute(device);
                                  setIsAddAttributeModalOpen(true);
                                }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-primary hover:bg-teal-600 rounded transition-colors"
                                title="Ajouter un attribut"
                              >
                                <span className="material-icons-outlined text-sm mr-1">add</span>
                                Ajouter
                              </button>
                            </div>
                            {deviceId && deviceAttributes[deviceId] && deviceAttributes[deviceId].length > 0 ? (
                              <div className="space-y-1">
                                {deviceAttributes[deviceId]
                                  .filter((attribute: Attribute) => {
                                    // Double-check that attribute belongs to this device
                                    const attrDeviceId = attribute.device?.id || (attribute.device as any)?.id;
                                    if (process.env.NODE_ENV === 'development' && attrDeviceId !== deviceId) {
                                      console.warn(`Attribute ${attribute.id} (${attribute.name}) belongs to device ${attrDeviceId}, not ${deviceId}`);
                                    }
                                    return attrDeviceId === deviceId || !attrDeviceId; // Allow if device ID is missing (shouldn't happen)
                                  })
                                  .map((attribute: Attribute) => {
                                  const attributeType = typeof attribute.type === 'string' 
                                    ? attribute.type 
                                    : (attribute.type as any)?.code || (attribute.type as any)?.name || '';
                                  const attributeTypeName = typeof attribute.type === 'object' && attribute.type
                                    ? (attribute.type as AttributeType).name || (attribute.type as AttributeType).code
                                    : attributeType;
                                  const attributeTypeUnit = typeof attribute.type === 'object' && attribute.type
                                    ? (attribute.type as AttributeType).unit
                                    : null;
                                  
                                  // Format current value with unit if available
                                  const displayValue = attribute.currentValue !== undefined && attribute.currentValue !== null
                                    ? `${attribute.currentValue}${attributeTypeUnit ? attributeTypeUnit : ''}`
                                    : null;
                                  
                                  // Ensure status is a boolean
                                  // Handle different possible formats: boolean true, string "true", or undefined/null
                                  let attributeStatus = false;
                                  if (attribute.status === true || attribute.status === 'true' || attribute.status === 1) {
                                    attributeStatus = true;
                                  } else if (attribute.status === false || attribute.status === 'false' || attribute.status === 0 || attribute.status === null || attribute.status === undefined) {
                                    attributeStatus = false;
                                  }
                                  
                                  // Debug log to check attribute data
                                  if (process.env.NODE_ENV === 'development') {
                                    console.log('Attribute rendering:', {
                                      id: attribute.id,
                                      name: attribute.name,
                                      status: attribute.status,
                                      statusType: typeof attribute.status,
                                      attributeStatus,
                                      fullAttribute: attribute
                                    });
                                  }
                                  
                                  return (
                                    <div 
                                      key={attribute.id} 
                                      className="flex items-center justify-between gap-3 py-2 px-2 hover:bg-slate-100/50 dark:hover:bg-[#1E293B]/50 rounded transition-colors"
                                      onClick={(e) => {
                                        // Prevent click from bubbling to parent
                                        e.stopPropagation();
                                      }}
                                    >
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        {/* Teal circle indicator */}
                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                          attributeStatus ? 'bg-teal-500' : 'bg-red-500'
                                        }`}></div>
                                        
                                        {/* Attribute name */}
                                        <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                                          {attribute.name}
                                        </span>
                                        
                                        {/* Current value (aligned to right) */}
                                        {displayValue && (
                                          <span className="text-sm text-slate-600 dark:text-slate-400 ml-auto">
                                            {displayValue}
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Edit button */}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (handleEditAttributeClick) {
                                            handleEditAttributeClick(attribute).catch((error) => {
                                              console.error('Error in handleEditAttributeClick:', error);
                                            });
                                          }
                                        }}
                                        className="p-1.5 text-slate-400 hover:text-primary transition-colors rounded hover:bg-slate-200 dark:hover:bg-[#1E293B] flex-shrink-0"
                                        title="Modifier l'attribut"
                                      >
                                        <span className="material-icons-outlined text-base">edit</span>
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-500 dark:text-slate-400 text-center py-2">
                                Aucun attribut
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                  Aucun appareil connecté
                </p>
              )}
            </div>
          </div>
        </div>
        </>
      )}

      {/* Add/Edit Device Modal */}
      {(isAddDeviceModalOpen || isEditDeviceModalOpen) && selectedBox && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => {
            setIsAddDeviceModalOpen(false);
            setIsEditDeviceModalOpen(false);
            setEditingDevice(null);
            resetDevice();
            deviceFormInitialLoad.current = true;
          }}
        >
          <div 
            className="w-full max-w-2xl bg-[#1e293b] rounded-xl shadow-2xl border border-[#1E293B] overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-white/5">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <span className="material-icons-outlined text-primary">
                  {isEditDeviceModalOpen ? 'edit' : 'add_circle'}
                </span>
                {isEditDeviceModalOpen ? 'Modifier l\'équipement' : 'Nouvel équipement'}
              </h2>
              <button
                onClick={() => {
                  setIsAddDeviceModalOpen(false);
                  setIsEditDeviceModalOpen(false);
                  setEditingDevice(null);
                  resetDevice();
                  deviceFormInitialLoad.current = true;
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleSubmitDevice(onSubmitDevice)} className="p-6 md:p-8">
              <div className="space-y-6">
                {/* Hidden code field - auto-generated but not visible */}
                <input type="hidden" {...registerDevice('code')} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Nom de l'équipement <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...registerDevice('name')}
                      className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary placeholder-slate-600 transition-shadow outline-none"
                      placeholder="Ex: Sonde Température #1"
                    />
                    {deviceErrors.name && (
                      <p className="mt-1 text-sm text-red-400">{deviceErrors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">UUID</label>
                    <Controller
                      name="uuid"
                      control={controlDevice}
                      render={({ field }) => (
                        <input
                          {...field}
                          className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary placeholder-slate-600 transition-shadow outline-none font-mono text-sm"
                          placeholder="UUID optionnel"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Box <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="box"
                      control={controlDevice}
                      render={({ field }) => (
                        <select
                          {...field}
                          key={`box-select-${isEditDeviceModalOpen ? editingDevice?.id : 'new'}-${field.value}`}
                          className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary outline-none appearance-none"
                        >
                          <option value="">Sélectionner une box...</option>
                          {allBoxes.map((box) => (
                            <option key={box.id} value={String(box.id)}>
                              {box.name} ({box.code})
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {deviceErrors.box && (
                      <p className="mt-1 text-sm text-red-400">{deviceErrors.box.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Type d'équipement <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="type"
                      control={controlDevice}
                      render={({ field }) => (
                        <select
                          {...field}
                          key={`type-select-${isEditDeviceModalOpen ? editingDevice?.id : 'new'}-${field.value}`}
                          className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary outline-none appearance-none"
                        >
                          <option value="">Sélectionner...</option>
                          {deviceTypes.map((type) => (
                            <option key={type.id} value={String(type.id)}>
                              {type.type}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {deviceErrors.type && (
                      <p className="mt-1 text-sm text-red-400">{deviceErrors.type.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </form>
            <div className="px-6 py-4 bg-slate-900/50 border-t border-slate-700/50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsAddDeviceModalOpen(false);
                  setIsEditDeviceModalOpen(false);
                  setEditingDevice(null);
                  resetDevice();
                  deviceFormInitialLoad.current = true;
                }}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors border border-transparent hover:border-slate-600"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmitDevice(onSubmitDevice)}
                disabled={createDeviceMutation.isPending || updateDeviceMutation.isPending}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 shadow-lg shadow-teal-500/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="material-icons-outlined text-lg">save</span>
                {isEditDeviceModalOpen
                  ? updateDeviceMutation.isPending
                    ? 'Modification...'
                    : 'Modifier'
                  : createDeviceMutation.isPending
                    ? 'Création...'
                    : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Attribute Modal */}
      {((isAddAttributeModalOpen && selectedDeviceForAttribute && selectedBox) || (isEditAttributeModalOpen && editingAttribute && selectedBox)) && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => {
            setIsAddAttributeModalOpen(false);
            setIsEditAttributeModalOpen(false);
            setSelectedDeviceForAttribute(null);
            setEditingAttribute(null);
            resetAttribute();
          }}
        >
          <div 
            className="w-full max-w-2xl bg-[#1e293b] rounded-xl shadow-2xl border border-[#1E293B] overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-white/5">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <span className="material-icons-outlined text-primary">{isEditAttributeModalOpen ? 'edit' : 'add_circle'}</span>
                {isEditAttributeModalOpen ? 'Modifier l\'attribut' : 'Nouvel Attribut'}
              </h2>
              <button
                onClick={() => {
                  setIsAddAttributeModalOpen(false);
                  setIsEditAttributeModalOpen(false);
                  setSelectedDeviceForAttribute(null);
                  setEditingAttribute(null);
                  resetAttribute();
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>
            <form 
              onSubmit={handleSubmitAttribute((data) => {
                console.log('onSubmitAttribute called with data:', data);
                console.log('isEditAttributeModalOpen:', isEditAttributeModalOpen);
                console.log('editingAttribute:', editingAttribute);
                onSubmitAttribute(data);
              })} 
              className="p-6 md:p-8"
            >
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...registerAttribute('name')}
                      className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary placeholder-slate-600 transition-shadow outline-none"
                      placeholder="Ex: Température salon"
                    />
                    {attributeErrors.name && (
                      <p className="mt-1 text-sm text-red-400">{attributeErrors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="type"
                      control={controlAttribute}
                      render={({ field }) => (
                        <select
                          {...field}
                          key={editingAttribute ? `edit-${editingAttribute.id}-${field.value}` : `create-${field.value}`}
                          className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary outline-none appearance-none"
                        >
                          <option value="">Sélectionner...</option>
                          {attributeTypes.map((type) => (
                            <option key={type.id} value={String(type.id)}>
                              {type.name} {type.unit ? `(${type.unit})` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {attributeErrors.type && (
                      <p className="mt-1 text-sm text-red-400">{attributeErrors.type.message}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">UUID</label>
                    <Controller
                      name="uuid"
                      control={controlAttribute}
                      render={({ field }) => (
                        <input
                          {...field}
                          className="w-full bg-[#0B1116] border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-primary focus:border-primary placeholder-slate-600 transition-shadow outline-none font-mono text-sm"
                          placeholder="UUID optionnel"
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4 bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-white">Visible</span>
                    <span className="text-xs text-slate-400">Afficher l'attribut</span>
                  </div>
                  <Controller
                    name="visible"
                    control={controlAttribute}
                    render={({ field }) => (
                      <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                        <input
                          type="checkbox"
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="sr-only"
                          id="attribute_visible"
                        />
                        <label
                          htmlFor="attribute_visible"
                          className={`block w-10 h-5 rounded-full cursor-pointer transition-colors ${
                            field.value ? 'bg-primary' : 'bg-slate-600'
                          }`}
                        >
                          <span
                            className={`block h-5 w-5 rounded-full bg-white shadow-md transform transition-transform ${
                              field.value ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </label>
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="px-6 py-4 bg-slate-900/50 border-t border-slate-700/50 flex items-center justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddAttributeModalOpen(false);
                    setIsEditAttributeModalOpen(false);
                    setSelectedDeviceForAttribute(null);
                    setEditingAttribute(null);
                    resetAttribute();
                  }}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors border border-transparent hover:border-slate-600"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createAttributeMutation.isPending || updateAttributeMutation.isPending}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 shadow-lg shadow-teal-500/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-icons-outlined text-lg">save</span>
                  {isEditAttributeModalOpen
                    ? updateAttributeMutation.isPending
                      ? 'Modification...'
                      : 'Modifier'
                    : createAttributeMutation.isPending
                      ? 'Création...'
                      : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
      <Toast
        message={deviceToastMessage}
        type={deviceToastType}
        isVisible={showDeviceToast}
        onClose={() => setShowDeviceToast(false)}
      />
      <Toast
        message={attributeToastMessage}
        type={attributeToastType}
        isVisible={showAttributeToast}
        onClose={() => setShowAttributeToast(false)}
      />
    </div>
  );
}
